import React, { useEffect, useRef, useState } from "react";
import {
  MultiFormatReader, DecodeHintType, BarcodeFormat,
  BinaryBitmap, HybridBinarizer, HTMLCanvasElementLuminanceSource,
} from "@zxing/library";
import { X, Camera, Bug, MagnifyingGlassPlus, Flashlight } from "@phosphor-icons/react";

const ENGINE_KEY = "pdv_scan_engine";   // auto | native | zxing
const ZOOM_KEY = "pdv_scan_zoom";
const DIAG_KEY = "pdv_scan_diag";
const SHARP_KEY = "pdv_scan_sharpen";

const NATIVE_FORMATS = ["code_128", "code_39", "ean_13", "ean_8", "upc_a", "upc_e", "itf", "codabar"];
const ZX_FORMATS = [
  BarcodeFormat.CODE_128, BarcodeFormat.CODE_39, BarcodeFormat.EAN_13, BarcodeFormat.EAN_8,
  BarcodeFormat.UPC_A, BarcodeFormat.UPC_E, BarcodeFormat.ITF, BarcodeFormat.CODABAR,
];
const FMT_NAME = {
  [BarcodeFormat.CODE_128]: "CODE_128", [BarcodeFormat.CODE_39]: "CODE_39",
  [BarcodeFormat.EAN_13]: "EAN_13", [BarcodeFormat.EAN_8]: "EAN_8",
  [BarcodeFormat.UPC_A]: "UPC_A", [BarcodeFormat.UPC_E]: "UPC_E",
  [BarcodeFormat.ITF]: "ITF", [BarcodeFormat.CODABAR]: "CODABAR",
};

function buildZxingReader() {
  const hints = new Map();
  hints.set(DecodeHintType.POSSIBLE_FORMATS, ZX_FORMATS);
  hints.set(DecodeHintType.TRY_HARDER, true);
  const r = new MultiFormatReader();
  r.setHints(hints);
  return r;
}

function applySharpen(ctx, w, h) {
  const src = ctx.getImageData(0, 0, w, h);
  const d = src.data;
  const out = ctx.createImageData(w, h);
  const o = out.data;
  const k = [0, -1, 0, -1, 5, -1, 0, -1, 0];
  for (let y = 1; y < h - 1; y++) {
    for (let x = 1; x < w - 1; x++) {
      let r = 0, g = 0, b = 0, idx = 0;
      for (let ky = -1; ky <= 1; ky++) {
        for (let kx = -1; kx <= 1; kx++) {
          const p = ((y + ky) * w + (x + kx)) * 4;
          const kv = k[idx++];
          r += d[p] * kv; g += d[p + 1] * kv; b += d[p + 2] * kv;
        }
      }
      const p = (y * w + x) * 4;
      o[p] = r < 0 ? 0 : r > 255 ? 255 : r;
      o[p + 1] = g < 0 ? 0 : g > 255 ? 255 : g;
      o[p + 2] = b < 0 ? 0 : b > 255 ? 255 : b;
      o[p + 3] = 255;
    }
  }
  ctx.putImageData(out, 0, 0);
}

export function BarcodeScanner({ open, onDetected, onClose, feedback }) {
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const streamRef = useRef(null);
  const trackRef = useRef(null);
  const detectorRef = useRef(null);
  const zxingRef = useRef(null);
  const rafRef = useRef(0);
  const lastRef = useRef({ code: "", ts: 0 });       // dedupe do mesmo código
  const lastActivityRef = useRef(0);                  // p/ fallback 500ms
  const lastDecodeRef = useRef(0);
  const fpsRef = useRef({ n: 0, t: 0 });
  const confRef = useRef({ code: "", count: 0 });

  const [engine, setEngine] = useState(() => localStorage.getItem(ENGINE_KEY) || "auto");
  const [zoom, setZoom] = useState(() => Number(localStorage.getItem(ZOOM_KEY)) || 1);
  const [diag, setDiag] = useState(() => localStorage.getItem(DIAG_KEY) === "1");
  const [sharpen, setSharpen] = useState(() => localStorage.getItem(SHARP_KEY) !== "0");
  const [nativeOk, setNativeOk] = useState(false);
  const [flash, setFlash] = useState(false);
  const [torchOn, setTorchOn] = useState(false);
  const [torchSupported, setTorchSupported] = useState(false);
  const [err, setErr] = useState("");
  const [diagInfo, setDiagInfo] = useState({ format: "-", content: "-", confidence: "-", fps: 0, algo: "-" });

  const engineRef = useRef(engine); engineRef.current = engine;
  const zoomRef = useRef(zoom); zoomRef.current = zoom;
  const diagRef = useRef(diag); diagRef.current = diag;
  const sharpRef = useRef(sharpen); sharpRef.current = sharpen;

  useEffect(() => { localStorage.setItem(ENGINE_KEY, engine); }, [engine]);
  useEffect(() => { localStorage.setItem(DIAG_KEY, diag ? "1" : "0"); }, [diag]);
  useEffect(() => { localStorage.setItem(SHARP_KEY, sharpen ? "1" : "0"); }, [sharpen]);
  useEffect(() => {
    localStorage.setItem(ZOOM_KEY, String(zoom));
    // zoom óptico nativo, se suportado
    const tr = trackRef.current;
    try {
      const caps = tr && tr.getCapabilities ? tr.getCapabilities() : null;
      if (caps && caps.zoom) {
        const z = Math.min(caps.zoom.max, Math.max(caps.zoom.min, zoom * (caps.zoom.max >= 3 ? 1 : caps.zoom.max / 3)));
        tr.applyConstraints({ advanced: [{ zoom: z }] }).catch(() => {});
      }
    } catch { /* ignore */ }
  }, [zoom]);

  useEffect(() => {
    if (!open) return;
    let active = true;
    setErr("");
    setTorchOn(false);
    setTorchSupported(false);
    zxingRef.current = buildZxingReader();
    lastActivityRef.current = performance.now();
    fpsRef.current = { n: 0, t: performance.now() };

    (async () => {
      // BarcodeDetector nativo (se houver), filtrando formatos suportados
      try {
        if ("BarcodeDetector" in window) {
          const supported = await window.BarcodeDetector.getSupportedFormats();
          const fmts = NATIVE_FORMATS.filter((f) => supported.includes(f));
          if (fmts.length) { detectorRef.current = new window.BarcodeDetector({ formats: fmts }); setNativeOk(true); }
        }
      } catch { detectorRef.current = null; }

      try {
        const stream = await navigator.mediaDevices.getUserMedia({
          video: { facingMode: { ideal: "environment" }, width: { ideal: 1280 }, height: { ideal: 720 } },
          audio: false,
        });
        if (!active) { stream.getTracks().forEach((t) => t.stop()); return; }
        streamRef.current = stream;
        trackRef.current = stream.getVideoTracks()[0];
        const v = videoRef.current;
        v.srcObject = stream;
        v.setAttribute("playsinline", "true");
        await v.play();
        // Detecta suporte a lanterna (torch) do dispositivo
        try {
          const caps = trackRef.current?.getCapabilities?.();
          if (active && caps && "torch" in caps && caps.torch) setTorchSupported(true);
        } catch { /* ignore */ }
        rafRef.current = requestAnimationFrame(tick);
      } catch (e) {
        setErr("Não foi possível acessar a câmera. Verifique as permissões (HTTPS obrigatório).");
      }
    })();

    const handleHit = (text, format, algo) => {
      const now = performance.now();
      lastActivityRef.current = now;
      if (text === lastRef.current.code && now - lastRef.current.ts < 2000) {
        return; // dedupe: mesmo código em janela curta
      }
      lastRef.current = { code: text, ts: now };
      // confiança heurística (estabilidade de leitura)
      if (confRef.current.code === text) confRef.current.count += 1;
      else confRef.current = { code: text, count: 1 };
      const conf = confRef.current.count >= 2 ? "alta" : "média";
      setDiagInfo((d) => ({ ...d, format, content: text, confidence: conf, algo }));
      setFlash(true); setTimeout(() => active && setFlash(false), 220);
      try { if (navigator.vibrate) navigator.vibrate(60); } catch { /* ignore */ }
      onDetected(text);
    };

    const tick = async () => {
      if (!active) return;
      const v = videoRef.current, canvas = canvasRef.current;
      if (!v || !canvas || v.readyState < 2) { rafRef.current = requestAnimationFrame(tick); return; }
      const now = performance.now();
      // FPS de decodificação
      const fp = fpsRef.current;
      if (now - fp.t >= 1000) { if (diagRef.current) setDiagInfo((d) => ({ ...d, fps: fp.n })); fp.n = 0; fp.t = now; }
      if (now - lastDecodeRef.current < 60) { rafRef.current = requestAnimationFrame(tick); return; }
      lastDecodeRef.current = now; fp.n += 1;

      // ROI central (faixa horizontal), com zoom digital
      const vw = v.videoWidth, vh = v.videoHeight;
      const z = zoomRef.current;
      const roiW = (vw * 0.86) / z, roiH = (vh * 0.42) / z;
      const sx = (vw - roiW) / 2, sy = (vh - roiH) / 2;
      const cw = Math.min(560, Math.round(roiW));
      const ch = Math.max(1, Math.round(cw * roiH / roiW));
      canvas.width = cw; canvas.height = ch;
      const ctx = canvas.getContext("2d", { willReadFrequently: true });
      ctx.filter = "grayscale(1) contrast(1.7) brightness(1.05)";
      ctx.drawImage(v, sx, sy, roiW, roiH, 0, 0, cw, ch);
      ctx.filter = "none";
      if (sharpRef.current) { try { applySharpen(ctx, cw, ch); } catch { /* ignore */ } }

      let hit = null, algo = "";
      const eng = engineRef.current;
      const useNative = detectorRef.current && (eng === "native" || eng === "auto");
      if (useNative) {
        try {
          const res = await detectorRef.current.detect(canvas);
          if (res && res.length) { hit = { text: String(res[0].rawValue || "").trim(), format: (res[0].format || "").toUpperCase() }; algo = "BarcodeDetector"; }
        } catch { /* ignore frame */ }
      }
      const idle = now - lastActivityRef.current;
      const useZxing = eng === "zxing" || !detectorRef.current || (eng === "auto" && !hit && idle > 500);
      if (!hit && useZxing) {
        try {
          const src = new HTMLCanvasElementLuminanceSource(canvas);
          const bmp = new BinaryBitmap(new HybridBinarizer(src));
          const r = zxingRef.current.decode(bmp);
          if (r) { hit = { text: String(r.getText() || "").trim(), format: FMT_NAME[r.getBarcodeFormat()] || "?" }; algo = "ZXing"; }
        } catch { /* NotFound */ } finally { try { zxingRef.current.reset(); } catch { /* ignore */ } }
      }
      if (diagRef.current && !hit) {
        setDiagInfo((d) => ({ ...d, algo: useZxing ? (algo || "ZXing (buscando)") : "BarcodeDetector (buscando)" }));
      }
      if (hit && hit.text) handleHit(hit.text, hit.format, algo);
      rafRef.current = requestAnimationFrame(tick);
    };

    return () => {
      active = false;
      cancelAnimationFrame(rafRef.current);
      const s = streamRef.current;
      if (s) s.getTracks().forEach((t) => t.stop());
      streamRef.current = null; trackRef.current = null; detectorRef.current = null; zxingRef.current = null;
    };
  }, [open]);

  if (!open) return null;
  const fb = feedback || {};

  const toggleTorch = async () => {
    const tr = trackRef.current;
    if (!tr) return;
    const next = !torchOn;
    try {
      await tr.applyConstraints({ advanced: [{ torch: next }] });
      setTorchOn(next);
    } catch {
      setErr("Não foi possível controlar a lanterna neste dispositivo.");
    }
  };

  return (
    <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-[60] p-4" data-testid="barcode-scanner-modal">
      <div className="bg-white max-w-md w-full p-5 max-h-[95vh] overflow-y-auto">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2 font-medium"><Camera size={18} /> Leitura contínua</div>
          <div className="flex items-center gap-3">
            <button onClick={() => setDiag((d) => !d)} className={`flex items-center gap-1 text-[10px] tracking-widest uppercase ${diag ? "text-[#3F6B45]" : "text-[#5C5A57]"}`} data-testid="scanner-diag-toggle"><Bug size={16} /> Diag</button>
            <button onClick={onClose} className="text-[#5C5A57] hover:text-black" data-testid="scanner-close-btn"><X size={20} /></button>
          </div>
        </div>

        <div className="relative bg-black rounded overflow-hidden" style={{ aspectRatio: "4 / 3" }}>
          <video ref={videoRef} muted playsInline className="w-full h-full object-cover" style={{ transform: `scale(${zoom})`, transformOrigin: "center" }} data-testid="scanner-video" />
          <canvas ref={canvasRef} className="hidden" />
          {/* Guia da área de leitura (ROI) */}
          <div className="absolute left-[7%] right-[7%] top-[29%] h-[42%] border-2 rounded pointer-events-none transition-colors" style={{ borderColor: flash ? "#38C172" : "rgba(255,255,255,0.7)" }} data-testid="scanner-roi" />
          {flash && <div className="absolute inset-0 bg-[#38C172]/15 pointer-events-none" data-testid="scanner-flash" />}
          {torchSupported && (
            <button onClick={toggleTorch} type="button" aria-label="Lanterna"
              className={`absolute right-3 top-3 grid h-11 w-11 place-items-center rounded-full backdrop-blur-sm transition-colors active:scale-95 ${torchOn ? "bg-amber-400 text-black shadow-[0_0_16px_rgba(251,191,36,0.7)]" : "bg-black/50 text-white hover:bg-black/70"}`}
              data-testid="scanner-torch-btn">
              <Flashlight size={20} weight={torchOn ? "fill" : "regular"} />
            </button>
          )}
        </div>

        {torchSupported && (
          <div className="mt-2 text-[11px] text-[#5C5A57]" data-testid="scanner-torch-hint">
            {torchOn ? "Lanterna ligada · toque para desligar" : "Ambiente escuro? Toque no ícone da lanterna."}
          </div>
        )}

        {/* Zoom */}
        <div className="flex items-center gap-2 mt-3">
          <MagnifyingGlassPlus size={16} className="text-[#5C5A57]" />
          <input type="range" min="1" max="3" step="0.1" value={zoom} onChange={(e) => setZoom(Number(e.target.value))} className="flex-1" data-testid="scanner-zoom" />
          <span className="text-xs tabular w-8 text-right">{zoom.toFixed(1)}x</span>
        </div>

        {/* Configurações do motor */}
        <div className="flex flex-wrap items-center gap-3 mt-3 text-xs">
          <label className="flex items-center gap-1">
            <span className="text-[#5C5A57]">Motor:</span>
            <select value={engine} onChange={(e) => setEngine(e.target.value)} className="input-underline" data-testid="scanner-engine">
              <option value="auto">Automático{nativeOk ? "" : " (ZXing)"}</option>
              <option value="native" disabled={!nativeOk}>BarcodeDetector</option>
              <option value="zxing">ZXing</option>
            </select>
          </label>
          <label className="flex items-center gap-1 cursor-pointer">
            <input type="checkbox" checked={sharpen} onChange={(e) => setSharpen(e.target.checked)} data-testid="scanner-sharpen" />
            <span>Nitidez</span>
          </label>
        </div>

        {/* Feedback do último item */}
        {fb.status === "ok" && (
          <div className="mt-3 p-2 bg-[#EAF3EC] border border-[#B7D8BF] text-xs text-[#2E5A38]" data-testid="scanner-feedback-ok">✓ <strong>{fb.name}</strong> adicionado · qtd <strong>{fb.qty}</strong></div>
        )}
        {fb.status === "error" && (
          <div className="mt-3 p-2 bg-[#F4E6E5] border border-[#DDA7A5] text-xs text-[#7A2D2B]" data-testid="scanner-feedback-error">⚠ Código <strong>{fb.code}</strong> não encontrado.</div>
        )}

        {/* Diagnóstico */}
        {diag && (
          <div className="mt-3 p-3 bg-[#0E0E0E] text-[#C8F6D2] font-mono text-[11px] leading-relaxed rounded" data-testid="scanner-diagnostics">
            <div>algoritmo: <span data-testid="diag-algo">{diagInfo.algo}</span></div>
            <div>formato: <span data-testid="diag-format">{diagInfo.format}</span></div>
            <div>conteúdo: <span data-testid="diag-content">{diagInfo.content}</span></div>
            <div>confiança: <span data-testid="diag-confidence">{diagInfo.confidence}</span></div>
            <div>fps: <span data-testid="diag-fps">{diagInfo.fps}</span> · nativo: {nativeOk ? "sim" : "não"}</div>
          </div>
        )}

        {err ? (
          <div className="mt-3 p-2 bg-[#F4E6E5] border border-[#DDA7A5] text-[11px] text-[#7A2D2B]" data-testid="scanner-error">{err}</div>
        ) : (
          <div className="mt-3 text-[11px] text-[#5C5A57]">Aponte o código dentro da faixa. Bipe vários itens em sequência.</div>
        )}
        <button onClick={onClose} className="brand w-full mt-3" data-testid="scanner-done-btn">Concluir</button>
      </div>
    </div>
  );
}

export default BarcodeScanner;
